// TSPL RTEXT to BITMAP converter - Converts RTEXT commands with non-ASCII characters to BITMAP commands
import iconv from 'iconv-lite';

interface TextCommandParams {
  x: number;
  y: number;
  font: string;
  rotation: number;
  xMul: number;
  yMul: number;
  text: string;
}

// Check if text contains non-ASCII characters
export function hasNonAscii(text: string): boolean {
  return /[^\x00-\x7F]/.test(text);
}

// Parse TSPL RTEXT command - Format: RTEXT x,y,"font",rotation,x_mul,y_mul,"text"
function parseRTextCommand(line: string): TextCommandParams | null {
  const trimmed = line.trim();
  if (!trimmed.toUpperCase().startsWith('RTEXT ')) return null;
  const params = trimmed.substring(6);
  const match = params.match(
    /^\s*(-?\d+)\s*,\s*(-?\d+)\s*,\s*"([^"]+)"\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*"([^"]*)"\s*$/
  );
  if (!match) return null;
  return {
    x: parseInt(match[1]),
    y: parseInt(match[2]),
    font: match[3],
    rotation: parseInt(match[4]),
    xMul: parseInt(match[5]),
    yMul: parseInt(match[6]),
    text: match[7]
  };
}

// 203 DPI (8 dots/mm): 1 DOT = 1 pixel
function getFontSize(yMul: number): number {
  return yMul;
}

// Get CSS font family - use specified font with system-ui fallback
function getFontFamily(font: string): string {
  return `${font}, system-ui, sans-serif`;
}

// Render text to canvas with rotation support
async function textToCanvas(
  text: string,
  fontSize: number,
  rotation: number = 0,
  fontFamily: string = 'Arial, sans-serif',
  bold: boolean = true
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const fontWeight = bold ? '600' : 'normal';
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  const metrics = ctx.measureText(text);
  const textWidth = Math.ceil(metrics.width);
  const textHeight = Math.ceil(fontSize * 1.2);
  let canvasWidth: number;
  let canvasHeight: number;
  if (rotation === 90 || rotation === 270) {
    canvasWidth = Math.ceil(textHeight / 8) * 8;
    canvasHeight = Math.ceil(textWidth / 8) * 8;
  } else {
    canvasWidth = Math.ceil(textWidth / 8) * 8;
    canvasHeight = textHeight;
  }
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  ctx.save();
  ctx.fillStyle = '#000000';
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  ctx.textBaseline = 'top';
  switch (rotation) {
    case 90:
      ctx.translate(canvasWidth, 0);
      ctx.rotate(90 * Math.PI / 180);
      ctx.fillText(text, 0, 0);
      break;
    case 180:
      ctx.translate(canvasWidth, canvasHeight);
      ctx.rotate(180 * Math.PI / 180);
      ctx.fillText(text, 0, 0);
      break;
    case 270:
      ctx.translate(0, canvasHeight);
      ctx.rotate(270 * Math.PI / 180);
      ctx.fillText(text, 0, 0);
      break;
    default:
      ctx.fillText(text, 0, 0);
  }
  ctx.restore();
  return canvas;
}

// Convert canvas to TSPL monochrome bitmap format
function canvasToTSPLBitmap(canvas: HTMLCanvasElement): Uint8Array {
  const ctx = canvas.getContext('2d')!;
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  const width = canvas.width;
  const height = canvas.height;
  const widthInBytes = Math.ceil(width / 8);
  const data: number[] = [];
  for (let y = 0; y < height; y++) {
    for (let b = 0; b < widthInBytes; b++) {
      let byte = 0;
      let mask = 128;
      for (let x = b * 8; x < (b + 1) * 8; x++) {
        if (x < width) {
          const idx = (y * width + x) * 4;
          const gray = (pixels[idx] + pixels[idx + 1] + pixels[idx + 2]) / 3;
          if (gray > 128 || pixels[idx + 3] < 128) byte ^= mask;
        } else {
          byte ^= mask;
        }
        mask >>= 1;
      }
      data.push(byte);
    }
  }
  return new Uint8Array(data);
}

// Generate BITMAP command with coordinate transformation (transforms TEXT coordinates to BITMAP top-left coordinates)
function canvasToBitmapCommand(
  canvas: HTMLCanvasElement,
  x: number,
  y: number,
  rotation: number
): { command: string; bitmapData: Uint8Array } {
  const bitmap = canvasToTSPLBitmap(canvas);
  const widthInBytes = Math.ceil(canvas.width / 8);
  let bitmapX = x;
  let bitmapY = y;
  switch (rotation) {
    case 0:
      bitmapX = x;
      bitmapY = y;
      break;
    case 90:
      bitmapX = x;
      bitmapY = y - canvas.height;
      break;
    case 180:
      bitmapX = x - canvas.width;
      bitmapY = y - canvas.height;
      break;
    case 270:
      bitmapX = x - canvas.width;
      bitmapY = y;
      break;
  }
  const command = `BITMAP ${bitmapX},${bitmapY},${widthInBytes},${canvas.height},0,`;
  return { command, bitmapData: bitmap };
}

// Convert RTEXT command to BITMAP
export async function convertRTextToBitmap(line: string): Promise<{
  converted: boolean;
  command?: string;
  bitmapData?: Uint8Array;
}> {
  const params = parseRTextCommand(line);
  if (!params) return { converted: false };
  try {
    const fontSize = getFontSize(params.yMul);
    const fontFamily = getFontFamily(params.font);
    const canvas = await textToCanvas(params.text, fontSize, params.rotation, fontFamily);
    const { command, bitmapData } = canvasToBitmapCommand(canvas, params.x, params.y, params.rotation);
    return { converted: true, command, bitmapData };
  } catch (error) {
    console.error('Failed to convert RTEXT to BITMAP:', error);
    return { converted: false };
  }
}

// Check if line is RTEXT command
function isRTextCommand(line: string): boolean {
  return line.toUpperCase().startsWith('RTEXT ');
}

// Process TSPL commands and convert all RTEXT to BITMAP
export async function processTSPLCommands(commands: string): Promise<Uint8Array> {
  const lines = commands.split(/\r\n|\r|\n/);
  const encoder = new TextEncoder();
  const parts: Uint8Array[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (isRTextCommand(trimmed)) {
      const result = await convertRTextToBitmap(trimmed);
      if (result.converted && result.command && result.bitmapData) {
        parts.push(encoder.encode(result.command));
        parts.push(result.bitmapData);
        parts.push(encoder.encode('\r\n'));
      } else {
        parts.push(new Uint8Array(iconv.encode(trimmed + '\r\n', 'gb18030')));
      }
    } else {
      parts.push(new Uint8Array(iconv.encode(trimmed + '\r\n', 'gb18030')));
    }
  }
  const totalLength = parts.reduce((sum, part) => sum + part.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }
  return result;
}

// Check if TSPL commands contain non-ASCII RTEXT commands
export function hasNonAsciiText(commands: string): boolean {
  const lines = commands.split(/\r\n|\r|\n/);
  for (const line of lines) {
    const trimmed = line.trim();
    if (isRTextCommand(trimmed)) {
      const params = parseRTextCommand(trimmed);
      if (params && hasNonAscii(params.text)) return true;
    }
  }
  return false;
}
